import { Keypair, PublicKey, Transaction, VersionedTransaction } from '@solana/web3.js';
import { WalletReadyState } from '@solana/wallet-adapter-base';

// DEV MODE ONLY - Hardcoded test wallet
// This keypair is for local testing only. DO NOT use in production!
// You can replace this with your own test wallet's secret key bytes
const DEV_WALLET_SECRET = new Uint8Array([
  // This is a random test keypair - replace with your own for testing
  174, 47, 154, 16, 202, 193, 206, 113, 199, 190, 53, 133, 169, 175, 31, 56,
  222, 53, 138, 189, 224, 216, 117, 173, 10, 149, 53, 45, 73, 251, 237, 246,
  15, 185, 186, 82, 177, 240, 148, 69, 241, 227, 167, 80, 141, 89, 240, 121,
  121, 35, 172, 247, 68, 251, 226, 218, 48, 63, 176, 109, 168, 89, 238, 135,
]);

// Enable dev mode via environment variable or localStorage
export const isDevMode = (): boolean => {
  if (typeof window !== 'undefined') {
    return (
      process.env.REACT_APP_DEV_WALLET === 'true' ||
      localStorage.getItem('devWalletEnabled') === 'true'
    );
  }
  return false;
};

// Toggle dev mode
export const setDevMode = (enabled: boolean): void => {
  localStorage.setItem('devWalletEnabled', enabled ? 'true' : 'false');
  window.location.reload();
};

// Get dev keypair
export const getDevKeypair = (): Keypair => {
  return Keypair.fromSecretKey(DEV_WALLET_SECRET);
};

// Get dev public key
export const getDevPublicKey = (): PublicKey => {
  return getDevKeypair().publicKey;
};

// Simplified wallet context interface for dev mode
export interface DevWalletContextState {
  autoConnect: boolean;
  connected: boolean;
  connecting: boolean;
  disconnecting: boolean;
  publicKey: PublicKey | null;
  wallet: {
    adapter: {
      name: string;
      publicKey: PublicKey | null;
      connected: boolean;
      readyState: WalletReadyState;
    };
    readyState: WalletReadyState;
  } | null;
  wallets: never[];
  select: () => void;
  connect: () => Promise<void>;
  disconnect: () => Promise<void>;
  sendTransaction: (transaction: Transaction) => Promise<string>;
  signTransaction: <T extends Transaction | VersionedTransaction>(tx: T) => Promise<T>;
  signAllTransactions: <T extends Transaction | VersionedTransaction>(txs: T[]) => Promise<T[]>;
  signMessage: (message: Uint8Array) => Promise<Uint8Array>;
}

// Create a mock wallet context for dev mode
export const createDevWalletContext = (): DevWalletContextState => {
  const keypair = getDevKeypair();

  return {
    autoConnect: true,
    connected: true,
    connecting: false,
    disconnecting: false,
    publicKey: keypair.publicKey,
    wallet: {
      adapter: {
        name: 'Dev Wallet',
        publicKey: keypair.publicKey,
        connected: true,
        readyState: WalletReadyState.Installed,
      },
      readyState: WalletReadyState.Installed,
    },
    wallets: [],
    select: () => {},
    connect: async () => {},
    disconnect: async () => {},
    sendTransaction: async (transaction: Transaction) => {
      // In dev mode, we sign with the dev keypair
      transaction.sign(keypair);
      return 'dev-mode-signature';
    },
    signTransaction: async <T extends Transaction | VersionedTransaction>(tx: T): Promise<T> => {
      if (tx instanceof Transaction) {
        tx.sign(keypair);
      } else if (tx instanceof VersionedTransaction) {
        tx.sign([keypair]);
      }
      return tx;
    },
    signAllTransactions: async <T extends Transaction | VersionedTransaction>(txs: T[]): Promise<T[]> => {
      for (const tx of txs) {
        if (tx instanceof Transaction) {
          tx.sign(keypair);
        } else if (tx instanceof VersionedTransaction) {
          tx.sign([keypair]);
        }
      }
      return txs;
    },
    signMessage: async (message: Uint8Array): Promise<Uint8Array> => {
      // Simple mock - in real dev testing you'd use nacl
      return message;
    },
  };
};

// Dev wallet for Anchor
export const getDevAnchorWallet = () => {
  const keypair = getDevKeypair();

  return {
    publicKey: keypair.publicKey,
    signTransaction: async <T extends Transaction | VersionedTransaction>(tx: T): Promise<T> => {
      if (tx instanceof Transaction) {
        tx.sign(keypair);
      } else if (tx instanceof VersionedTransaction) {
        tx.sign([keypair]);
      }
      return tx;
    },
    signAllTransactions: async <T extends Transaction | VersionedTransaction>(txs: T[]): Promise<T[]> => {
      for (const tx of txs) {
        if (tx instanceof Transaction) {
          tx.sign(keypair);
        } else if (tx instanceof VersionedTransaction) {
          tx.sign([keypair]);
        }
      }
      return txs;
    },
  };
};

// Log dev wallet info
export const logDevWalletInfo = (): void => {
  const keypair = getDevKeypair();
  console.log('=== DEV WALLET MODE ===');
  console.log('Public Key:', keypair.publicKey.toBase58());
  console.log('To disable: localStorage.removeItem("devWalletEnabled") and refresh');
  console.log('========================');
};
